import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
import {
  getProtocolById,
  getProtocolByShorthand,
  getProtocols,
  type Protocol,
  type ProtocolListResponse,
} from '@/pages/api/scribeAPIV2';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { skipToken, useQuery } from '@tanstack/react-query';
import { useQueryUser } from './useQueryUser';

// Query keys
export const GET_PROTOCOLS = 'getProtocols';
export const GET_PROTOCOL_BY_ID = 'getProtocolById';
export const GET_PROTOCOL_BY_SHORTHAND = 'getProtocolByShorthand';

/**
 * Get cached protocols data
 */
export const getCurrentProtocols = () =>
  reactQueryClient.getQueryData<ProtocolListResponse>([GET_PROTOCOLS]);

/**
 * Invalidate protocols cache (list + individual protocol queries)
 */
export const invalidateProtocols = async () => {
  await Promise.all([
    reactQueryClient.invalidateQueries({
      queryKey: [GET_PROTOCOLS],
    }),
    reactQueryClient.invalidateQueries({
      queryKey: [GET_PROTOCOL_BY_ID],
    }),
  ]);
};

/**
 * Hook to fetch all protocols (system + user)
 */
export function useQueryProtocols() {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: userData } = useQueryUser();

  return useQuery({
    queryKey: [GET_PROTOCOLS],
    queryFn:
      isAuthenticated && !!userData
        ? async () => {
            const { data } = await getProtocols();
            return data;
          }
        : skipToken,
  });
}

/**
 * Hook to fetch a single protocol by ID
 */
export function useQueryProtocolById(protocolId: string | undefined) {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: userData } = useQueryUser();

  return useQuery({
    queryKey: [GET_PROTOCOL_BY_ID, protocolId],
    queryFn:
      isAuthenticated && !!userData && !!protocolId
        ? async () => {
            const { data } = await getProtocolById(protocolId);
            return data;
          }
        : skipToken,
    enabled: !!protocolId,
  });
}

/**
 * Hook to fetch a protocol by shorthand (for autocomplete)
 */
export function useQueryProtocolByShorthand(shorthand: string | undefined) {
  const { isAuthenticated } = useIsAuthenticated();
  const { data: userData } = useQueryUser();

  return useQuery({
    queryKey: [GET_PROTOCOL_BY_SHORTHAND, shorthand],
    queryFn:
      isAuthenticated && !!userData && !!shorthand
        ? async () => {
            const { data } = await getProtocolByShorthand(shorthand);
            return data;
          }
        : skipToken,
    enabled: !!shorthand && shorthand.length >= 2,
    retry: false, // Don't retry on 404
  });
}

/**
 * Helper to get all protocols as a flat list with system flag
 */
export function useProtocolsList() {
  const { data, isLoading, error } = useQueryProtocols();

  const allProtocols: Protocol[] = [];
  if (data) {
    // Add system protocols first
    allProtocols.push(...data.system_protocols);
    // Add user protocols
    allProtocols.push(...data.user_protocols);
  }

  return {
    protocols: allProtocols,
    systemProtocols: data?.system_protocols ?? [],
    userProtocols: data?.user_protocols ?? [],
    isLoading,
    error,
  };
}
